<template>
	<div class="fd-suggester fd-suggester-desktop">
		<div class="fd-suggester-query" ref="segments" :class="{ 'fd-suggester-query-disabled':ro }">
			<template v-for="(item, index) in editorSegments" :key="item.key">
				<tag-input
					v-if="segmentIsEditor(item)"
					:tabindex="index+1"
					v-model="item.query"
					:disabled="ro || !canAddMoreTags"
					:min-width="isLastIndex(index) && canAddMoreTags ? (tagCount === 0 ? 80 : 20) : 2"
					:datakey="item.key"
					class="fd-suggester-input"
					@focus="editorInFocus(index)"
					@blur="editorBlurred(index)"
					@keydown="keyDown"></tag-input>
				<tag-part
					v-else-if="segmentIsTag(item)"
					:tabindex="index+1"
					:src-type="sgp.srcType"
					:src-id="item.id"
					:name="item.name"
					:icon="item.icon"
					:href="item.href"
					:title="item.title"
					:key="item.key"
					:index="index"
					:disabled="ro"
					@delete="deleteTag(index)"
					@edit="editTag(index)"
					class="fd-suggester-tag"></tag-part>
			</template>
		</div>
		<drop-down class="fd-cats-drop-down" ref="dd" style="padding: 4px 0">
			<table class="fd-results" v-if="haveResults">
				<tr :class="resultRowClass(index)" v-for="(item, index) in results" @mouseenter="selectRow(index)" @click.stop="doAction">
					<td class="fd-result-cell" v-for="col in item.columns">
						<template v-if="col.type === 'text'">{{ col.content }}</template>
						<template v-else-if="col.type === 'html'"><span v-html="col.content"></span></template>
						<template v-else-if="col.type === 'icon'">
							<sym-icon :name="col.content"></sym-icon>
						</template>
						<template v-else-if="col.type === 'img'"><img :src="col.content" :width="col.width" :height="col.height" class="fd-suggest-image"/></template>
						<span v-else>Unknown result type <code>{{ col.type }}</code></span>
					</td>
					<td v-if="needExpandColumn" class="fd-result-cell fd-expend-cell">
						<icon-button v-if="item.expand" name="boxed-arrow-right" @click.stop="expandWith(item.expand)"></icon-button>
					</td>
				</tr>
			</table>
			<div v-else-if="!canAddMoreTags" class="fd-no-results">{{ env.msg('suggest.no.more') }}</div>
			<div v-else-if="emptyState === 'type'" class="fd-no-results">{{ env.msg('suggest.please.type') }}</div>
			<div v-else-if="emptyState === 'not-found'" class="fd-no-results">{{ env.msg('suggest.not.found', notFoundQuery) }}</div>
			<div v-else class="fd-no-results">{{ env.msg('suggest.error') }}</div>
		</drop-down>
	</div>
</template>

<script setup lang="ts">
import useSuggester, {type EditorSegment, type NormalizedSuggestionItem, processResultItem, type Segment, type SuggesterProps, suggesterProps, type TagSegment} from "./Suggester";
import TagInput from "./TagInput.vue";
import TagPart from "./TagPart.vue";
import SymIcon from "../../helpers/SymIcon.vue";
import {computed, nextTick, type Ref, ref, watch} from "vue";
import {injectEnv} from "../../Environment";
import type {Suggester} from "@t/fd/editor/Suggester";
import type {Suggestions} from "@t/search/Suggester";
import DropDown from "../DropDown.vue";
import IconButton from "../../helpers/IconButton.vue";

const LIST_SIZE = 20;

const uprops = defineProps({...suggesterProps});
const props = uprops as SuggesterProps;

const emit = defineEmits(["suggested"]);

const sgp = computed<Suggester>(() => props.part as Suggester);

const env = injectEnv();
const {visible, value, clazz, style, ro, tagArray, propagateSuggestion, deleteData, canAddMoreTags, tagCount, isDuplicate} = useSuggester(props, env);


const segmentId = ref(0);
const editors: Ref<EditorSegment[]> = ref([]); // queries go here
const focused = ref(-1); // editor in focus (index to editors)
const expandQuery = ref("");
const results = ref<NormalizedSuggestionItem[]>([]);

const emptyState = ref<string>("type");   // why are results empty
const notFoundQuery = ref<string>("");   // why are results empty

const selrow = ref(-1); /* null = nothing selected */
const segments = ref<HTMLDivElement | null>(null);
const dd = ref<InstanceType<typeof DropDown> | null>(null);


const query = computed(() => {
	if (expandQuery.value.length > 0) return expandQuery.value;
	else if (focused.value >= 0 && focused.value < editors.value.length) {
		return editors.value[focused.value].query;
	}
	return "";
});

const fetchUrl = computed(() => {
	let url = sgp.value.url;
	if (sgp.value.suggester) url = env.net.urlParam(url, "suggester", sgp.value.suggester);
	url = env.net.urlParam(url, "query", query.value);
	return url;
});

const rows = computed<number>(() => {
	return results.value.length;
});

const selected = computed<NormalizedSuggestionItem | null>(() => {
	if (selrow.value >= 0 && selrow.value < results.value.length) return results.value[selrow.value];
	return null;
});

const needExpandColumn = computed(() => {
	for (let item of results.value) {
		if (item.expand) return true;
	}
	return false;
});

const haveResults = computed(() => {
	return results.value.length > 0;
});

const emptyQuery = computed(() => {
	if (focused.value >= 0) return editors.value[focused.value].query.length === 0;
	return true;
});

const editorSegments = computed<Segment[]>(() => {
	const segs: Segment[] = [];
	let edIdx = 0;
	for (let tag of tagArray.value) {
		segs.push(editorAt(edIdx++));
		segs.push(tag);
	}
	segs.push(editorAt(edIdx));
	return segs;
});

watch(() => query.value, (newQuery, oldQuery) => {
	if (newQuery.length > 2) {
		loadSuggestions();
	} else {
		nextTick(() => {
			results.value.length = 0; // not enough data
			emptyState.value = "type";
		});
	}
});

function segmentIsEditor(segment: Segment): segment is EditorSegment {
	return segment.type === 'editor';
}

function segmentIsTag(segment: Segment): segment is TagSegment {
	return segment.type === 'tag';
}

async function loadSuggestions() {
	try {
		const q = query.value;  // what we are asking for
		const res = await env.net.fetch(fetchUrl.value);
		const results: Suggestions = await res.json();
		processResults(results, q);
	} catch (error) {
		console.error("Error! Could not reach the suggester.", error);
	}
}

function processResults(suggestions: Suggestions, qry: string) {
	results.value.length = 0;
	selrow.value = -1;
	const instr = suggestions.instructions || {};
	for (let ritem of suggestions.items) {
		results.value.push(processResultItem(ritem, instr));
	}
	if (results.value.length === 0) {
		emptyState.value = "not-found";
		notFoundQuery.value = qry;
	}
}


/* Key generating - for efficient list rendering */
function nextId(): string {
	return "seg" + ++segmentId.value;
}

function isLastIndex(idx: number) {
	return idx === editorSegments.value.length - 1;
}

function deleteTag(segmentIndex: number, query = "") {
	if (ro.value) return; // cannot edit
	const tagSegment = editorSegments.value[segmentIndex];
	const dataIndex = tagSegment.index;
	// before deleting tag, we have to also delete one of editors: B the one before tag   A the one after tag
	// B is preferred and A only if B is not empty and A is
	const editorSegmentBefore = editorSegments.value[segmentIndex - 1] as EditorSegment;
	const editorSegmentAfter = editorSegments.value[segmentIndex + 1] as EditorSegment;
	let deleteEdIdx: number;
	let keptEditor:EditorSegment;
	if (editorSegmentBefore.query.length > 0 && editorSegmentAfter.query.length === 0) {
		deleteEdIdx = editorSegmentAfter.index;
		keptEditor = editorSegmentBefore;
	} else {
		deleteEdIdx = editorSegmentBefore.index;
		keptEditor = editorSegmentAfter;
	}
	if (query) keptEditor.query = query;

	deleteEditor(deleteEdIdx);
	deleteData(dataIndex);

	setTimeout(() => focusEditor(deleteEdIdx, true), 100);
	// nextTick(() => focusEditor(deleteEdIdx));   ??? doesn't work be I don't know why
}

function editTag(segmentIndex: number) {
	const tagSegment = editorSegments.value[segmentIndex] as TagSegment;
	deleteTag(segmentIndex, tagSegment.name);
}

function focusEditor(editorIdx: number, cursorAtEnd = false) {
	const containerElement = segments.value;
	if (containerElement) {
		let editorSpan = containerElement.querySelector("[data-key=" + editors.value[editorIdx].key + "]");
		if (editorSpan) {
			const el = editorSpan as HTMLSpanElement;
			el.focus();
			if (cursorAtEnd) {
				const selection = window.getSelection();
				if (selection) {
					const range = document.createRange();
					range.selectNodeContents(el); // select all
					range.collapse(false);        // collapse to the end
					selection.removeAllRanges();
					selection.addRange(range);
				}
			}
		}
	}
	if (dd.value != null && !ro.value) {
		dd.value.repositionDropDown(segments);
	}
}

function focusLastEditor() {
	nextTick(() => {
		const len = tagArray.value.length;
		focusEditor(len);
	});
}

function editorAt(idx: number): EditorSegment {
	while (idx >= editors.value.length) editors.value.push(newEditor(idx));
	return editors.value[idx];
}

/****** Initialization ******/

function resultRowClass(index: number): string[] {
	const classes = ["fd-result"];
	if (index === selrow.value) classes.push("fd-result-selected");
	return classes;
}


function selectRow(idx: number) {
	selrow.value = idx;
}

/***** Event handling *****/
function editorInFocus(segmentIndex: number) {
	focused.value = editorSegments.value[segmentIndex].index; // convert and store as index to editors.value
	if (dd.value != null && !ro.value) {
		dd.value.editorFocus(segments);
	}
}

function editorBlurred(index: number) {
	// focused.value = -1;
	// expandQuery.value = "";
	if (dd.value != null && !ro.value) {
		dd.value.editorBlur();
	}
}

function editorMouseEnter(index: number) {
	if (dd.value != null && !ro.value) {
		dd.value.editorMouseEnter();
	}
}

function editorMouseLeave(index: number) {
	if (dd.value != null && !ro.value) {
		dd.value.editorMouseLeave();
	}
}

function expandWith(query: string) {
	expandQuery.value = query;
}

function keyDown(e: KeyboardEvent) {
	if (e.keyCode === 38 /* UP */) {
		e.preventDefault();
		if (rows.value > 0) {
			if (selrow.value <= 0) selectRow(rows.value - 1); // select last
			else selectRow(selrow.value - 1);
		}
	} else if (e.keyCode === 40 /* DOWN */) {
		e.preventDefault();
		if (rows.value > 0) {
			if (selrow.value >= rows.value - 1) selectRow(0); // select first
			else selectRow(selrow.value + 1);
		}
	} else if (e.keyCode === 37 /* LEFT */) {
		// collapse row (if expanded)
		if (expandQuery.value) {
			e.preventDefault();
			expandQuery.value = "";
		}
	} else if (e.keyCode === 39 /* RIGHT */) {
		// expand row
		const item = selected.value;
		if (item) {
			if (item.expand) {
				e.preventDefault();
				expandWith(item.expand);
			}
		}
	} else if (e.keyCode === 13 /* ENTER */) {
		e.preventDefault();
		doAction();
	} else if (e.keyCode === 27 /* ESC */) {
		results.value.length = 0;
		selrow.value = -1;
		expandQuery.value = "";
	}
}

function newEditor(idx: number): EditorSegment {
	return {
		key: nextId(),
		type: "editor",
		query: "",
		index: idx
	};
}

function deleteEditor(idx: number) {
	if (idx < editors.value.length) {
		editors.value.splice(idx, 1);
		reindexEditors(idx);
	}
}

function insertEditor(idx: number) {
	editors.value.splice(idx, 0, newEditor(idx));
	reindexEditors(idx + 1);
}

function reindexEditors(fromIdx: number) {
	for (let i = fromIdx; i < editors.value.length; i++) {
		editors.value[i].index = i;
	}
}

function doAction() {
	let selection = selected.value;
	if (selection != null) {
		if (!sgp.value.allowDuplicates && isDuplicate(selection)) return;
		editors.value[focused.value].query = "";
		insertEditor(focused.value);
		propagateSuggestion(selection, focused.value);
		focused.value++;
		const fc = focused.value;
		emit("suggested");
		nextTick(() => {
			focusEditor(fc);
		});
	}
}

defineExpose({
	focusLastEditor
});
</script>

<style>
.fd-suggester-desktop {
	position: relative;
	display: inline-block;
	flex-grow: 1;
}


.fd-suggester-query {
	display: flex;
	justify-content: flex-start;
	align-items: baseline;
	flex-wrap: wrap;
	column-gap: 0; /* should be filled with the editor */
	row-gap: 1px;
	min-height: var(--input-height);

	background-color: var(--bg);
	color: var(--fg);
	border-radius: var(--input-radius);
	box-shadow: var(--input-offset-shadow);

	min-width: 80px;
	overflow-y: auto;
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
	padding: 0;
	border-left: 3px solid var(--border-color);

	&:focus-visible,
	&:focus-within,
	&:focus {
		outline: none;
		box-shadow: 0 0 0 1px var(--input-bg), 0 0 2px 3px var(--outline-color);
	}

	&.fd-suggester-query-disabled {
		color: var(--input-disabled-fg);
		background-color: var(--input-disabled-bg);
	}
}


.fd-suggester-input {
	line-height: var(--input-height);
}

table.fd-results {
	background-color: var(--bg);
	color: var(--fg);
	max-width: 1000px;
	overflow-y: auto;
	overflow-x: hidden;
	border-collapse: collapse;
}

.fd-result {
	cursor: pointer;
}

.fd-result-selected {
	background-color: var(--bg);
	color: var(--fg);
}

.fd-result-cell {
	padding: 4px 4px;
}

.fd-result-cell:first-child {
	padding-left: 8px;
}

.fd-result-cell:last-child {
	padding-right: 8px;
}

.fd-no-results {
	padding-left: 8px;
	padding-right: 8px;
	font-size: 12px;
	opacity: 0.5;
}
</style>
